import React from 'react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { VisuallyHidden } from '@/components/ui/visually-hidden';

interface ModalFooterProps {
  currentStep: number;
  totalSteps: number;
  stepIsValid: boolean;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  onCancel: () => void;
  isMobile?: boolean; // Optional prop for explicit mobile layouts
}

/**
 * Footer component for the medication dialog
 * Handles back, next, cancel and submit actions with appropriate button states
 */
/**
 * Footer component for the medication dialog
 * Handles back, next, cancel and submit actions with appropriate button states
 * 
 * @component
 * @param {number} currentStep - Current step in the form flow
 * @param {number} totalSteps - Total number of steps in the form
 * @param {boolean} stepIsValid - Whether the current step has valid inputs
 * @param {Function} onBack - Handler for back button
 * @param {Function} onNext - Handler for next button
 * @param {Function} onSubmit - Handler for submit button
 * @param {Function} onCancel - Handler for cancel button
 * @param {boolean} [isMobile] - Whether component is rendering on mobile
 */
const ModalFooter: React.FC<ModalFooterProps> = ({
  currentStep,
  totalSteps,
  stepIsValid,
  onBack,
  onNext,
  onSubmit,
  onCancel,
  isMobile = useIsMobile() // Use injected prop or fallback to hook for better testability
}) => {
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;

  return (
    <div className={cn(
      "sticky bottom-0 border-t border-border/20 py-4 px-4 flex items-center",
      "bg-background/95 backdrop-blur-sm shadow-[0_-2px_10px_rgba(0,0,0,0.05)]",
      "z-10 w-full", // Ensure footer is always on top and full width
      isMobile ? "justify-between fixed left-0 right-0 bottom-0" : "justify-end",
      // Mobile-specific fixes for smaller screens
      isMobile && "mt-auto pb-safe pt-3 pb-3 md:pb-4" // Add safe area padding for iOS devices
    )}
    role="region"
    aria-label="Dialog navigation">
      {/* Progress indicator for accessibility */}
      <VisuallyHidden>
        Step {currentStep} of {totalSteps}
      </VisuallyHidden>
      <div className="flex items-center">
        {!isFirstStep && (
          <Button
            variant="outline"
            onClick={onBack}
            size={isMobile ? "default" : "sm"}
            className={cn(
              "flex items-center gap-1 min-w-[80px] min-h-[40px]",
              isMobile ? "" : "mr-2"
            )}
            type="button"
            aria-label="Go back to previous step"
          >
            <ArrowLeft size={16} /> Back
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={onCancel}
          size={isMobile ? "default" : "sm"}
          type="button"
          className="flex items-center gap-1 min-h-[40px]"
          aria-label="Cancel medication setup"
        >
          <X size={16} /> Cancel
        </Button>
        
        {isLastStep ? (
          <Button
            onClick={onSubmit}
            disabled={!stepIsValid}
            size={isMobile ? "default" : "sm"}
            type="button"
            className={cn(
              "flex items-center gap-1 min-w-[100px] min-h-[40px]",
              !stepIsValid ? "opacity-50 cursor-not-allowed" : ""
            )}
            aria-label="Complete medication setup"
            aria-disabled={!stepIsValid}
          >
            <Check size={16} /> Complete
          </Button>
        ) : (
          <Button
            onClick={onNext}
            disabled={!stepIsValid}
            size={isMobile ? "default" : "sm"}
            type="button"
            className={cn(
              "flex items-center gap-1 min-w-[100px] min-h-[40px]",
              !stepIsValid ? "opacity-50 cursor-not-allowed" : ""
            )}
            aria-label="Continue to next step"
            aria-disabled={!stepIsValid}
          >
            Continue <ArrowRight size={16} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ModalFooter;
